/**
 * The area-specific part of Quality / External tools: what the organization's own tools say about this project.
 * SonarQube or SonarCloud (its quality gate, the conditions that failed, a link to its dashboard), the Semgrep AppSec
 * Platform, and the SARIF files imported from any other scanner (Snyk, GitHub or GitLab code scanning, DefectDojo…),
 * each with who imported it and when. Editors import a SARIF file (Import SARIF) and remove one; everyone downloads
 * the current findings as SARIF (Download SARIF), for another tool to read.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import DataTable from '../table/DataTable';
import { WhenBy } from '../table/TableParts';
import { ArrowSquareOut, DownloadSimple, Trash, UploadSimple } from '@phosphor-icons/react';
import ConfirmDialog from '../ConfirmDialog';
import { deleteSarifImport, downloadSarif, getExternal, importSarif } from '../../services/QualityService';
import { Pill } from './QualityCommon';
import './QualityTools.css';
import {
  SARIF_MAX_BYTES, SARIF_SOURCES, SOURCE_KINDS, errorText, gateStatusOf, plural, relativeTime, sarifSourceName, toolStateOf,
} from './qualityModel';

const byName = (by) => (by && typeof by === 'object' ? by.name || by.email : by) || null;
const toMs = (at) => (typeof at === 'number' ? at : Date.parse(at) || null);

/** The 400's errors as lines: `[{ path, message }]` or plain strings. */
function errorLines(error) {
  const list = error?.body?.errors;
  if (!Array.isArray(list) || !list.length) { return [errorText(error)]; }
  return list.map((e) => (typeof e === 'string' ? e : `${e.path ? `${e.path}: ` : ''}${e.message || ''}`));
}

/** Import SARIF: a .sarif or .json file, where it comes from, an optional name; the server's errors under it. */
export function SarifImportForm({ projectId, onImported, onCancel }) {
  const [file, setFile] = useState(null);
  const [source, setSource] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]);
  const submit = async (e) => {
    e.preventDefault();
    const problems = [];
    if (!file) { problems.push('Choose a SARIF file (.sarif or .json).'); }
    if (!source) { problems.push('Say which tool made it.'); }
    if (file && file.size > SARIF_MAX_BYTES) { problems.push('The file is over 10 MB.'); }
    setErrors(problems);
    if (problems.length) { return; }
    setBusy(true);
    try {
      const text = await file.text();
      const answer = await importSarif(projectId, { source, name, sarif: text });
      onImported(answer);
    } catch (err) {
      setErrors(errorLines(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form aria-label="Import SARIF" className="quality-card quality-pad quality-sarif-form" data-testid="quality-sarif-form" onSubmit={submit}>
      <p className="quality-muted quality-small">SARIF is the file format most scanners can save their results in. Its findings join this check until you remove the file.</p>
      <div className="quality-row">
        <label className="quality-field">
          <span>File</span>
          <input accept=".sarif,.json,application/json,application/sarif+json" aria-label="SARIF file" onChange={(e) => setFile(e.target.files?.[0] || null)} type="file" />
        </label>
        <label className="quality-field">
          <span>From</span>
          <select aria-label="Which tool made it" className="quality-input" onChange={(e) => setSource(e.target.value)} value={source}>
            <option value="">Choose…</option>
            {SARIF_SOURCES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </label>
        <label className="quality-field">
          <span>Name (optional)</span>
          <input aria-label="Name" className="quality-input" maxLength={120} onChange={(e) => setName(e.target.value)} placeholder="e.g. Snyk weekly scan" value={name} />
        </label>
      </div>
      {errors.length > 0 && (
        <ul className="quality-ink-fail quality-small" data-testid="quality-sarif-errors" role="alert">
          {errors.map((line, i) => <li key={i}>{line}</li>)}
        </ul>
      )}
      <div className="quality-row">
        <button className="quality-btn is-primary" disabled={busy} type="submit">{busy ? 'Importing…' : 'Import'}</button>
        <button className="quality-btn is-quiet" disabled={busy} onClick={onCancel} type="button">Cancel</button>
      </div>
    </form>
  );
}

SarifImportForm.propTypes = { onCancel: PropTypes.func.isRequired, onImported: PropTypes.func.isRequired, projectId: PropTypes.string.isRequired };

function SonarGate({ gate }) {
  const conditions = (gate.conditions || []).filter((c) => c.status && c.status !== 'OK');
  if (!conditions.length) { return null; }
  return (
    <ul aria-label="Quality gate conditions" className="quality-area-list quality-small">
      {conditions.map((c) => (
        <li key={c.metric}>
          <Pill className="is-xs" tone={gateStatusOf(c.status).tone}>{gateStatusOf(c.status).label}</Pill>
          {' '}<span className="quality-mono">{c.metric}</span>: {c.actual ?? '–'} (limit {c.comparator === 'LT' ? 'at least' : 'at most'} {c.threshold})
        </li>
      ))}
    </ul>
  );
}

SonarGate.propTypes = { gate: PropTypes.object.isRequired };

const NOT_USED = 'Not used by this project: turn it on in Settings › External tools.';
const NOT_SET_UP = 'Not set up for your organization: ask your Flow admin.';

/** The sources that ran, then SonarQube and Semgrep when the project does not use them (and why). */
function sourceRows(tools, org) {
  const rows = [...tools];
  if (org && !tools.some((s) => s.kind === 'sonar')) { rows.push({ id: 'org-sonar', name: 'SonarQube', placeholder: org.sonar?.configured ? NOT_USED : NOT_SET_UP }); }
  if (org && !tools.some((s) => s.kind === 'semgrep')) { rows.push({ id: 'org-semgrep', name: 'Semgrep AppSec Platform', placeholder: org.semgrep?.configured ? NOT_USED : NOT_SET_UP }); }
  return rows;
}

function sourceStatus(s) {
  if (s.placeholder) { return s.placeholder; }
  return s.gate ? `Quality gate: ${gateStatusOf(s.gate.status).label}` : toolStateOf(s.state).label;
}

const SOURCE_COLUMNS = [
  {
    key: 'name',
    label: 'Source',
    width: 220,
    value: (s) => s.name || SOURCE_KINDS[s.kind],
    title: (s) => `${s.name || SOURCE_KINDS[s.kind]}${s.tool?.version ? ` ${s.tool.version}` : ''}`,
    render: (s) => <>{s.name || SOURCE_KINDS[s.kind]}{s.tool?.version ? <span className="quality-muted quality-small"> {s.tool.version}</span> : null}</>,
  },
  {
    key: 'status',
    label: 'Status',
    wrap: 2,
    value: sourceStatus,
    title: (s) => [sourceStatus(s), s.message].filter(Boolean).join(': '),
    render: (s) => {
      if (s.placeholder) { return <span className="quality-muted quality-small">{s.placeholder}</span>; }
      const state = toolStateOf(s.state);
      const gate = s.gate ? gateStatusOf(s.gate.status) : null;
      return <>{gate ? <Pill tone={gate.tone}>Quality gate: {gate.label}</Pill> : <Pill tone={state.tone}>{state.label}</Pill>}{s.message ? <span className="quality-small"> {s.message}</span> : null}</>;
    },
  },
  { key: 'findings', label: 'Findings', width: 100, value: (s) => (s.state === 'done' ? s.findings ?? 0 : null), render: (s) => (s.state === 'done' ? s.findings ?? 0 : '–') },
  {
    // Not cut: the SonarQube gate's conditions and the link.
    key: 'details',
    label: 'Details',
    width: 240,
    clip: false,
    value: (s) => s.url || null,
    render: (s) => (
      <>
        {s.gate && <SonarGate gate={s.gate} />}
        {s.url && <a className="quality-btn is-sm is-quiet" href={s.url} rel="noopener noreferrer" target="_blank">Open in {s.kind === 'sonar' ? 'SonarQube' : 'Semgrep'}<ArrowSquareOut aria-hidden size={12} /></a>}
      </>
    ),
  },
];

const toolText = (imp) => (imp.tool?.name ? `${imp.tool.name}${imp.tool.version ? ` ${imp.tool.version}` : ''}` : '');
const SARIF_COLUMNS = [
  { key: 'source', label: 'From', width: 150, value: (imp) => sarifSourceName(imp.source) },
  {
    key: 'name',
    label: 'Name',
    value: (imp) => imp.name || '–',
    title: (imp) => [imp.name, toolText(imp)].filter(Boolean).join(' · '),
    render: (imp) => <>{imp.name || '–'}{toolText(imp) ? <span className="quality-muted quality-small"> · {toolText(imp)}</span> : null}</>,
  },
  { key: 'importedAt', label: 'Imported', width: 130, value: (imp) => (imp.importedAt ? toMs(imp.importedAt) : null), render: (imp) => <WhenBy at={imp.importedAt ? toMs(imp.importedAt) : undefined} text={imp.importedAt ? relativeTime(toMs(imp.importedAt)) : '–'} /> },
  { key: 'by', label: 'By', width: 170, value: (imp) => byName(imp.by) || '–' },
  { key: 'findings', label: 'Findings', width: 100, value: (imp) => imp.findings ?? null, render: (imp) => imp.findings ?? '–' },
];

export default function External({ check, projectId, canEdit = false, onChanged = null }) {
  const [external, setExternal] = useState(null);
  const [importing, setImporting] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const load = useCallback(async () => {
    try {
      const next = await getExternal(projectId);
      if (alive.current) { setExternal(next); }
    } catch {
      // An older sandbox: only what the check itself says.
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const sources = (check.details?.sources || []);
  const tools = sources.filter((s) => s.kind !== 'sarif');
  const imports = external?.imports || sources.filter((s) => s.kind === 'sarif').map((s) => ({ id: s.id, source: s.tool?.name || null, name: s.name, importedAt: s.importedAt, by: s.by, findings: s.findings }));
  const org = external?.org || null;

  const download = async () => {
    setNotice(null);
    try { await downloadSarif(projectId); } catch (e) { setNotice(`Could not download the SARIF file: ${errorText(e)}`); }
  };
  const imported = (answer) => {
    setImporting(false);
    setNotice(`Imported${answer?.import?.findings !== undefined ? `: ${plural(answer.import.findings, 'finding')}` : ''}.`);
    load();
    onChanged?.();
  };
  const remove = async () => {
    setBusy(true);
    try {
      await deleteSarifImport(projectId, removing.id);
      setNotice(`Removed ${removing.name || 'the import'}.`);
      setRemoving(null);
      load();
      onChanged?.();
    } catch (e) {
      setNotice(`Could not remove it: ${errorText(e)}`);
      setRemoving(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label="External tools" className="quality-area" data-testid="quality-external">
      <div className="quality-area-head">
        <p className="quality-muted quality-small quality-grow">
          SonarQube and the Semgrep AppSec Platform run when your Flow admin has set them up and this project uses them (Settings › External tools).
        </p>
        {canEdit && !importing && <button className="quality-btn is-sm" onClick={() => setImporting(true)} type="button"><UploadSimple aria-hidden size={13} />Import SARIF</button>}
        <button className="quality-btn is-sm" onClick={download} title="Every current finding of this project, in SARIF 2.1.0, for another tool to read" type="button"><DownloadSimple aria-hidden size={13} />Download SARIF</button>
      </div>
      {notice && <p className="quality-banner is-info" data-testid="quality-external-notice" role="status">{notice}</p>}
      {importing && <SarifImportForm onCancel={() => setImporting(false)} onImported={imported} projectId={projectId} />}

      <div className="quality-card quality-table-box">
        <DataTable
          ariaLabel="Sources"
          className="quality-table"
          columns={SOURCE_COLUMNS}
          empty={<span className="quality-muted">No external tool ran for this project.</span>}
          rowKey="id"
          rowProps={(s) => (s.placeholder ? {} : { 'data-testid': `quality-source-${s.id}` })}
          rows={sourceRows(tools, org)}
        />
      </div>

      <div className="quality-card quality-table-box">
        <DataTable
          actions={canEdit ? {
            label: 'Actions',
            width: 110,
            render: (imp) => <button aria-label={`Remove ${imp.name || sarifSourceName(imp.source)}`} className="quality-btn is-sm is-quiet" onClick={() => setRemoving(imp)} type="button"><Trash aria-hidden size={13} />Remove</button>,
          } : undefined}
          ariaLabel="Imported SARIF files"
          caption={<caption className="quality-area-caption">Imported SARIF files</caption>}
          className="quality-table"
          columns={SARIF_COLUMNS}
          empty={<span className="quality-muted">No SARIF file imported yet.</span>}
          rowKey="id"
          rowProps={(imp) => ({ 'data-testid': `quality-sarif-${imp.id}` })}
          rows={imports}
          storageKey="flow.quality.sarifImports.hiddenColumns"
          testId="quality-sarif-imports"
        />
      </div>

      <ConfirmDialog
        busy={busy}
        busyText="Removing…"
        confirmText="Remove"
        isOpen={Boolean(removing)}
        message={`The findings of ${removing?.name || 'this file'} (${sarifSourceName(removing?.source)}) leave the External tools check, and the release gate is checked again. You can import the file again later.`}
        onCancel={() => setRemoving(null)}
        onConfirm={remove}
        title="Remove the imported SARIF file?"
      />
    </section>
  );
}

External.propTypes = {
  canEdit: PropTypes.bool,
  check: PropTypes.object.isRequired,
  // () => void: after an import or a removal (the check and the overview read again).
  onChanged: PropTypes.func,
  projectId: PropTypes.string.isRequired,
};
